import React, { ErrorInfo, ReactNode } from "react";

import { logAnalyticsEvent, unlockAnalyticsQueue } from "@/constants/amplitude";
import { EventLoadStatus, WebEventProps, WebEventTypes } from "@/constants/event";
import { localStorageManager, LocalStorageManagerKeys } from "@/utils/localStorageManager";

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

const IMPORTING_MODULE_ERROR = "Importing a module script failed";

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({
            hasError: true,
            error,
            errorInfo,
        });

        console.error(error, errorInfo);

        // Track first funnel entry on error (only for first visit)
        const isFirstVisit = !localStorageManager.getStoredItem<boolean>(
            LocalStorageManagerKeys.IS_FIRST_VISIT_TRACKED
        );

        if (isFirstVisit) {
            // Unlock trackers WITHOUT flushing the queue yet
            unlockAnalyticsQueue(false);

            // Send FUNNEL_ENTRY with FAILED status (will go through immediately since unlocked)
            logAnalyticsEvent(WebEventTypes.FUNNEL_ENTRY, {
                [WebEventProps.LOAD_STATUS]: EventLoadStatus.FAILED,
                [WebEventProps.LOAD_ERROR]: error.message,
            });

            // Mark first visit as tracked to prevent duplicate events
            localStorageManager.storeItem(LocalStorageManagerKeys.IS_FIRST_VISIT_TRACKED, true);

            // Now flush any other queued events
            setTimeout(() => {
                unlockAnalyticsQueue();
            }, 10);
        }

        if (error.message.includes(IMPORTING_MODULE_ERROR)) {
            window.location.reload();
        }
    }

    render(): ReactNode {
        return this.props.children;
    }
}

export default ErrorBoundary;
